import { createContext, useContext, useEffect, useRef, useState } from "react";
import { App } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ActivityType, BlockType, ResourceType } from "../../../../types";
import type { ResourceResponse } from "../../../../types/api/getResources";
import type { ResourceEntity } from "../../../../services/resources/resources.types";
import type { ActivityEntityInput } from "../../../../services/activity/activity.types";
import {
  findActivityIndex,
  matchActivity,
  remapActivityIndexedRecord,
} from "../activityReorder";
import {
  toManipulativesActivity,
  toRegularActivity,
} from "../manipulativesConversion";
import {
  type FormLessonUI,
  type FormBlockUI,
  type FormActivityUI,
  type FormAlternativeUI,
  ActivityMainDevelopmentDomain,
  ActivityInteractionType,
  ActivityTypeOfEducationalActivity,
  initialLesson,
  parseLessonResponse,
  getActivitiesByType,
  validateForm,
  resolveActivityType,
  BLOCK_TYPE_OPTIONS,
} from "../utils";
import {
  addAlternative as addAlternativeToActivities,
  addAlternativeBlock as addAlternativeBlockToActivities,
  removeAlternative as removeAlternativeFromActivities,
  removeAlternativeBlock as removeAlternativeBlockFromActivities,
  updateAlternative as updateAlternativeInActivities,
  updateAlternativeBlock as updateAlternativeBlockInActivities,
} from "../alternativesState";
import { useLessons } from "../../../../hooks/useLessons";
import { useResources } from "../../../../hooks/useResource";
import { useNotification } from "../../../../hooks/useNotification";
import {
  customUpload,
  handleSubmitFormResponseManage,
} from "../../../../utils";
import {
  createLesson,
  LessonConflictError,
  ResourceHomeworkConflictError,
  updateLesson,
} from "../../../../services/lessons/lessons.service";
import ResourceHomeworkImpactModal from "../../../ResourceHomeworkImpactModal";
import type {
  HomeworkResolutionDto,
  ResourceHomeworkImpact,
} from "../../../../types/api/resourceHomeworkImpact";
import {
  generateActivitySummary,
  generateLessonSummary,
  generateManipulativeMaterials,
  sugerirPastillasIA,
} from "../../../../services/ai";
import {
  getPlanningSummaries,
  regeneratePlanningSuperSummary,
  regeneratePlanningSummary,
  type PlanningSummarySections,
  type PlanningSuperSummary,
  type PlanningSuperSummarySections,
} from "../../../../services/planning-summaries";
import {
  buildPlanningSummaryLessonFields,
  buildPlanningSuperSummaryLessonFields,
  resolvePlanningSummaryErrorNotification,
} from "./planningSummaryHelpers";

const emptyPlanningSummarySections = (): PlanningSummarySections => ({
  [ActivityType.START]: { text: null },
  [ActivityType.MIDDLE]: { text: null },
  [ActivityType.CLOSING]: { text: null },
});

const emptyPlanningSuperSummary = (): PlanningSuperSummary => ({
  text: null,
  manual: false,
  stale: false,
});

const emptyPlanningSuperSummarySections = (): PlanningSuperSummarySections => ({
  [ActivityType.START]: emptyPlanningSuperSummary(),
  [ActivityType.MIDDLE]: emptyPlanningSuperSummary(),
  [ActivityType.CLOSING]: emptyPlanningSuperSummary(),
});

// A backend that predates the super summary omits the field entirely.
const toPlanningSuperSummarySections = (
  sections: PlanningSummarySections,
): PlanningSuperSummarySections => ({
  [ActivityType.START]:
    sections[ActivityType.START].superSummary ?? emptyPlanningSuperSummary(),
  [ActivityType.MIDDLE]:
    sections[ActivityType.MIDDLE].superSummary ?? emptyPlanningSuperSummary(),
  [ActivityType.CLOSING]:
    sections[ActivityType.CLOSING].superSummary ?? emptyPlanningSuperSummary(),
});

export interface LessonFormContextValue {
  lesson: FormLessonUI;
  setLesson: React.Dispatch<React.SetStateAction<FormLessonUI>>;
  educationLevel: "KINDER" | "PRIMARIA" | undefined;
  setEducationLevel: React.Dispatch<
    React.SetStateAction<"KINDER" | "PRIMARIA" | undefined>
  >;
  pillSuggestions: Record<number, any>;
  setPillSuggestions: React.Dispatch<React.SetStateAction<Record<number, any>>>;
  pillLoading: Record<number, boolean>;
  setPillLoading: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  // Lección diagnóstico: omite la validación de "al menos una actividad por
  // tipo" y se persiste como Lesson.diagnostic (la plataforma la etiqueta
  // "Diagnóstico").
  diagnosticLesson: boolean;
  setDiagnosticLesson: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  /** Numeric lesson id, or null for a lesson not yet created. */
  currentLessonId: number | null;
  planningSummaries: PlanningSummarySections;
  /** Section type whose planning summary is being generated, if any. */
  generatingPlanningSummaryType: ActivityType | null;
  handleGeneratePlanningSummary: (activityType: ActivityType) => Promise<void>;
  handleChangePlanningSummaryText: (
    activityType: ActivityType,
    text: string,
  ) => void;
  planningSuperSummaries: PlanningSuperSummarySections;
  /** Section type whose super summary is being generated, if any. */
  generatingPlanningSuperSummaryType: ActivityType | null;
  handleGeneratePlanningSuperSummary: (
    activityType: ActivityType,
  ) => Promise<void>;
  handleChangePlanningSuperSummaryText: (
    activityType: ActivityType,
    text: string,
  ) => void;
  /** Id of the activity whose summary is being generated, if any. */
  generatingSummaryActivityId: number | null;
  isGeneratingLessonSummary: boolean;
  /** Id of the activity whose materials are being generated, if any. */
  generatingMaterialsActivityId: number | null;
  resourceTypeFilter: ResourceType | null;
  setResourceTypeFilter: React.Dispatch<
    React.SetStateAction<ResourceType | null>
  >;
  resource: ResourceResponse[] | null | undefined;
  fileUrls: Record<string, string>;
  setFileUrls: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  resources: ResourceResponse[];
  resourceDetails: Record<number, ResourceEntity>;
  // Actions
  addActivity: (type: ActivityType, positionIndex?: number) => void;
  removeActivity: (activityId: number) => void;
  reorderActivities: (activities: FormActivityUI[]) => void;
  addBlock: (activityId: number) => void;
  removeBlock: (activityIndex: number, blockIndex: number) => void;
  addSectionBlock: (activityIndex: number, sectionIndex: number) => void;
  removeSectionBlock: (
    activityIndex: number,
    sectionIndex: number,
    blockIndex: number,
  ) => void;
  handleSectionBlockUpdate: <K extends keyof FormBlockUI>(
    activityIndex: number,
    sectionIndex: number,
    blockIndex: number,
    key: K,
    value: any,
  ) => void;
  handleBlockUpdate: <K extends keyof FormBlockUI>(
    activityIndex: number,
    blockIndex: number,
    key: K,
    value: any,
  ) => void;
  handleMainDevelopmentDomains: (
    activityIndex: number,
    value: ActivityMainDevelopmentDomain[],
  ) => void;
  handleInteractionTypes: (
    activityIndex: number,
    value: ActivityInteractionType[],
  ) => void;
  handleTypesOfEducationalActivities: (
    activityIndex: number,
    value: ActivityTypeOfEducationalActivity[],
  ) => void;
  handleManipulativesToggle: (activityIndex: number, checked: boolean) => void;
  handleActivityUpdate: <K extends keyof ActivityEntityInput>(
    activityIndex: number,
    key: K,
    value: ActivityEntityInput[K],
  ) => void;
  handleGenerateSummary: (activityIndex: number) => Promise<void>;
  handleGenerateLessonSummary: () => Promise<void>;
  handleBlockTypeChange: (
    activityIndex: number,
    blockIndex: number,
    type: BlockType,
  ) => void;
  handleBlockMaterialsChange: (
    activityIndex: number,
    blockIndex: number,
    materials: string,
  ) => void;
  handleBlockTextChange: (
    activityIndex: number,
    blockIndex: number,
    text: string,
  ) => void;
  handleBlockFileIdChange: (
    activityIndex: number,
    blockIndex: number,
    file_id: string,
    fileUrl?: string,
  ) => void;
  haveFileIdInBlock: (
    activityIndex: number,
    blockIndex: number,
  ) => { haveFile: boolean; file_id: string };
  loadResourceDetails: (resourceId: number) => Promise<void>;
  handleResourceIdsChange: (
    activityIndex: number,
    blockIndex: number,
    resourceId: number | null,
  ) => void;
  handleResourceFileIdChange: (
    activityIndex: number,
    blockIndex: number,
    resourceFileId: number | null,
  ) => void;
  // SCRUM-795: alternativas sin manipulativos de una actividad manipulativa.
  addAlternative: (activityIndex: number) => void;
  removeAlternative: (activityIndex: number, alternativeIndex: number) => void;
  handleAlternativeUpdate: <K extends keyof FormAlternativeUI>(
    activityIndex: number,
    alternativeIndex: number,
    key: K,
    value: FormAlternativeUI[K],
  ) => void;
  addAlternativeBlock: (
    activityIndex: number,
    alternativeIndex: number,
  ) => void;
  removeAlternativeBlock: (
    activityIndex: number,
    alternativeIndex: number,
    blockIndex: number,
  ) => void;
  handleAlternativeBlockUpdate: <K extends keyof FormBlockUI>(
    activityIndex: number,
    alternativeIndex: number,
    blockIndex: number,
    key: K,
    value: FormBlockUI[K],
  ) => void;
  handleGenerateMaterials: (activityIndex: number) => Promise<void>;
  handleSubmit: () => Promise<void>;
  handleSuggestPills: (activityIndex: number) => Promise<void>;
  customUpload: typeof customUpload;
}

const LessonFormContext = createContext<LessonFormContextValue | null>(null);

export const useLessonForm = () => {
  const ctx = useContext(LessonFormContext);
  if (!ctx)
    throw new Error("useLessonForm must be used inside LessonFormProvider");
  return ctx;
};

export const LessonFormProvider: React.FC<{
  children: React.ReactNode;
  lessonId?: string;
}> = ({ children, lessonId: lessonIdProp }) => {
  const { id: routeLessonId } = useParams();
  const lessonId = lessonIdProp ?? routeLessonId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // From App.useApp(): the static notification does not render under React 19.
  const { notification } = App.useApp();
  const { notify } = useNotification();
  const { getLessonById } = useLessons();
  const { resources, getResourceById } = useResources();

  const [lesson, setLesson] = useState<FormLessonUI>(initialLesson);
  const [educationLevel, setEducationLevel] = useState<"KINDER" | "PRIMARIA" | undefined>(
    lesson.education_level ?? undefined,
  );

  // Sincroniza educationLevel con lesson.education_level
  useEffect(() => {
    setLesson((prev) => ({
      ...prev,
      education_level: educationLevel,
    }));
  }, [educationLevel]);
  const [pillSuggestions, setPillSuggestions] = useState<Record<number, any>>(
    {},
  );
  const [pillLoading, setPillLoading] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<string>(ActivityType.START);
  const [diagnosticLesson, setDiagnosticLesson] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatingSummaryActivityId, setGeneratingSummaryActivityId] =
    useState<number | null>(null);
  const [planningSummaries, setPlanningSummaries] =
    useState<PlanningSummarySections>(emptyPlanningSummarySections());
  const [generatingPlanningSummaryType, setGeneratingPlanningSummaryType] =
    useState<ActivityType | null>(null);
  // Snapshot of what the server last returned, so handleSubmit only PATCHes
  // sections the editor actually changed since load/last generation.
  const planningSummariesLoadedRef = useRef<PlanningSummarySections>(
    emptyPlanningSummarySections(),
  );
  const [planningSuperSummaries, setPlanningSuperSummaries] =
    useState<PlanningSuperSummarySections>(emptyPlanningSuperSummarySections());
  const [
    generatingPlanningSuperSummaryType,
    setGeneratingPlanningSuperSummaryType,
  ] = useState<ActivityType | null>(null);
  const planningSuperSummariesLoadedRef = useRef<PlanningSuperSummarySections>(
    emptyPlanningSuperSummarySections(),
  );
  const [isGeneratingLessonSummary, setIsGeneratingLessonSummary] =
    useState(false);
  const [generatingMaterialsActivityId, setGeneratingMaterialsActivityId] =
    useState<number | null>(null);
  const [resourceTypeFilter, setResourceTypeFilter] =
    useState<ResourceType | null>(ResourceType.VIDEO);
  const [resource, setResource] = useState<ResourceResponse[] | null>();
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [resourceDetails, setResourceDetails] = useState<
    Record<number, ResourceEntity>
  >({});
  const initialResourceDetailsLoadedRef = useRef(false);
  // updated_at as loaded; sent as expected_updated_at so the backend can
  // reject a save over someone else's concurrent edit (409 LESSON_MODIFIED).
  // No re-arm after save: success always leaves the page via navigate(-1).
  const lessonUpdatedAtRef = useRef<string | null>(null);
  // SCRUM-1020: last submitted PATCH payload (+ id), so a confirmed
  // resolution from the impact modal can retry the exact same save with
  // `homework_resolutions` attached, without re-running validateForm.
  const lastSubmitPayloadRef = useRef<{
    id: number | undefined;
    data: Record<string, unknown>;
  } | null>(null);
  const [resourceImpactModal, setResourceImpactModal] = useState<{
    impact: ResourceHomeworkImpact[];
  } | null>(null);
  const [isRetryingSubmit, setIsRetryingSubmit] = useState(false);

  useEffect(() => {
    setResource(resources);
  }, []);

  useEffect(() => {
    if (lesson.education_level) setEducationLevel(lesson.education_level);
    else setEducationLevel(undefined);
  }, [lesson.education_level]);

  useEffect(() => {
    const fetchInfo = async () => {
      setIsLoading(true);
      try {
        if (lessonId) {
          const response = await getLessonById(Number(lessonId));
          if (response) {
            lessonUpdatedAtRef.current = response.updated_at;
            const parsedLesson = parseLessonResponse(response);
            setLesson(parsedLesson);
            setDiagnosticLesson(!!response.diagnostic);
            // Populate pillSuggestions from saved activity pill fields
            const loadedPills: Record<number, any> = {};
            parsedLesson.activities.forEach((activity, index) => {
              if (
                (activity.bloom_level?.length ?? 0) > 0 ||
                (activity.primaria_interaction_type?.length ?? 0) > 0 ||
                (activity.evidence_type?.length ?? 0) > 0 ||
                (activity.activity_nature?.length ?? 0) > 0
              ) {
                loadedPills[index] = {
                  bloom_level: activity.bloom_level || [],
                  primaria_interaction_type:
                    activity.primaria_interaction_type || [],
                  evidence_type: activity.evidence_type || [],
                  activity_nature: activity.activity_nature || [],
                };
              }
            });
            setPillSuggestions(loadedPills);
            const urls: Record<string, string> = {};
            parsedLesson.activities.forEach((activity) => {
              activity.blocks.forEach((block) => {
                if (block.file_id && block.file_id_url)
                  urls[block.file_id] = block.file_id_url;
              });
              activity.sections?.forEach((section) => {
                section.blocks.forEach((block) => {
                  if (block.file_id && block.file_id_url)
                    urls[block.file_id] = block.file_id_url;
                });
              });
            });
            setFileUrls(urls);
          }
        }
      } catch {
        notification.error({
          message: "Error",
          description:
            "La información no pudo ser cargada, por favor notifica este error!",
          placement: "top",
          duration: 3,
        });
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };
    fetchInfo();
  }, [lessonId, notification]);

  // Separate effect: a summaries-fetch failure must not surface the generic
  // "lesson could not load" error and block editing the rest of the form.
  useEffect(() => {
    if (!lessonId) return;
    const fetchPlanningSummaries = async () => {
      try {
        const response = await getPlanningSummaries(Number(lessonId));
        setPlanningSummaries(response.sections);
        planningSummariesLoadedRef.current = response.sections;
        const superSummaries = toPlanningSuperSummarySections(response.sections);
        setPlanningSuperSummaries(superSummaries);
        planningSuperSummariesLoadedRef.current = superSummaries;
      } catch (error) {
        console.error("Error cargando resúmenes de planeación:", error);
      }
    };
    fetchPlanningSummaries();
  }, [lessonId]);

  // Eliminada la inicialización automática de actividades vacías.
  // Ahora, al crear una lección nueva, no habrá actividades por defecto.

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isInitialized || !lessonId || !lesson?.activities || !resources)
      return;
    lesson.activities.forEach((activity) => {
      const blocks = activity.has_manipulatives
        ? activity.sections.flatMap((s) => s.blocks)
        : activity.blocks;
      blocks.forEach((block) => {
        if (
          BLOCK_TYPE_OPTIONS[block.type]?.hasResource &&
          block.resourceId &&
          !resourceDetails[block.resourceId]
        ) {
          const sel = resources.find((r) => r.id === block.resourceId);
          if (sel?.type === ResourceType.DOWNLOAD)
            loadResourceDetails(block.resourceId);
        }
      });
    });
    initialResourceDetailsLoadedRef.current = true;
  }, [isInitialized, lessonId, resources, lesson]);

  // ── In-flight AI calls vs. a reordered array ────────────────────────────────
  // An AI handler captures its activityIndex before awaiting, and the array can
  // be reordered while the request is in flight. Writing back by that captured
  // index would land the result on a different activity, so the target is
  // re-resolved by id (falling back to the index for an activity with no id).

  const activitiesRef = useRef(lesson.activities);
  useEffect(() => {
    activitiesRef.current = lesson.activities;
  }, [lesson.activities]);

  const currentActivityIndex = (
    activity: FormActivityUI,
    capturedIndex: number,
  ) => findActivityIndex(activitiesRef.current, activity, capturedIndex);

  // ── Activity mutations ──────────────────────────────────────────────────────

  /**
   * Replaces `lesson.activities` with a rebuilt array (new order, new
   * activityType, added or removed entries). `pillSuggestions` and `pillLoading`
   * are keyed by the activity's index, so they must travel with the activity —
   * otherwise the pills would be saved onto a different one.
   */
  const reorderActivities = (activities: FormActivityUI[]) => {
    const previousActivities = lesson.activities;
    setPillSuggestions((prev) =>
      remapActivityIndexedRecord(prev, previousActivities, activities),
    );
    setPillLoading((prev) =>
      remapActivityIndexedRecord(prev, previousActivities, activities),
    );
    setLesson((prev) => ({ ...prev, activities }));
  };

  const addActivity = (type: ActivityType, positionIndex?: number) => {
    const newActivity = {
      id: Date.now(),
      title: "",
      duration: 0,
      activityType: type,
      is_homework: false,
      has_manipulatives: false,
      is_socioemotional: false,
      socioemotional_main_goal: "",
      socioemotional_support_goal: "",
      mainDevelopmentDomains: [],
      interactionTypes: [],
      typesOfEducationalActivities: [],
      blocks: [
        {
          id: Date.now() + 10000,
          type: BlockType.TEXT,
          order_by: 0,
          text: "",
          file_id: null,
          file_id_url: null,
          materials: "",
          resourceId: null,
          resourceFileId: null,
        },
      ],
      sections: [],
    };
    const others = lesson.activities.filter((a) => a.activityType !== type);
    const ofType = lesson.activities.filter((a) => a.activityType === type);
    if (positionIndex !== undefined) {
      ofType.splice(positionIndex + 1, 0, newActivity);
    } else {
      ofType.push(newActivity);
    }
    reorderActivities([...others, ...ofType]);
  };

  const removeActivity = (activityId: number) => {
    const target = lesson.activities.find((a) => a.id === activityId);
    if (!target) {
      console.error("¡Actividad no encontrada!");
      return;
    }
    // Validación eliminada: ahora se puede borrar la última actividad de cualquier sección
    reorderActivities(
      lesson.activities.filter((a) => a.id !== activityId),
    );
  };

  const addBlock = (activityId: number) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity) =>
        activity.id !== activityId
          ? activity
          : {
              ...activity,
              blocks: [
                ...activity.blocks,
                {
                  id: Date.now(),
                  type: BlockType.TEXT,
                  order_by: activity.blocks.length,
                  text: "",
                  file_id: null,
                  file_id_url: null,
                  materials: "",
                  resourceId: null,
                },
              ],
            },
      ),
    }));
  };

  const removeBlock = (activityIndex: number, blockIndex: number) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.filter((_, bi) => bi !== blockIndex),
            },
      ),
    }));
  };

  const addSectionBlock = (activityIndex: number, sectionIndex: number) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              sections: activity.sections.map((section, si) =>
                si !== sectionIndex
                  ? section
                  : {
                      ...section,
                      blocks: [
                        ...section.blocks,
                        {
                          id: Date.now(),
                          type: BlockType.TEXT,
                          order_by: section.blocks.length,
                          text: "",
                          file_id: null,
                          file_id_url: null,
                          materials: "",
                          resourceId: null,
                          resourceFileId: null,
                        },
                      ],
                    },
              ),
            },
      ),
    }));
  };

  const removeSectionBlock = (
    activityIndex: number,
    sectionIndex: number,
    blockIndex: number,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              sections: activity.sections.map((section, si) =>
                si !== sectionIndex
                  ? section
                  : {
                      ...section,
                      blocks: section.blocks.filter(
                        (_, bi) => bi !== blockIndex,
                      ),
                    },
              ),
            },
      ),
    }));
  };

  const loadResourceDetails = async (resourceId: number) => {
    if (resourceDetails[resourceId]) return;
    try {
      const details = await getResourceById(resourceId);
      if (details) {
        setResourceDetails((prev) => ({ ...prev, [resourceId]: details }));
        if (details.files && details.files.length === 1) {
          const singleFileId = details.files[0].id;
          setLesson((prev) => ({
            ...prev,
            activities: prev.activities.map((activity) => ({
              ...activity,
              blocks: activity.blocks.map((block) =>
                block.resourceId === resourceId && !block.resourceFileId
                  ? { ...block, resourceFileId: singleFileId }
                  : block,
              ),
              sections:
                activity.sections?.map((section) => ({
                  ...section,
                  blocks: section.blocks.map((block) =>
                    block.resourceId === resourceId && !block.resourceFileId
                      ? { ...block, resourceFileId: singleFileId }
                      : block,
                  ),
                })) || [],
            })),
          }));
        }
      }
    } catch (error) {
      console.error(`Error loading resource details for ${resourceId}:`, error);
    }
  };

  const handleSectionBlockUpdate = <K extends keyof FormBlockUI>(
    activityIndex: number,
    sectionIndex: number,
    blockIndex: number,
    key: K,
    value: any,
  ) => {
    if (key === "resourceId" && value) {
      const sel = resources.find((r) => r.id === (value as number));
      if (sel?.type === ResourceType.DOWNLOAD)
        loadResourceDetails(value as number);
    }
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              sections: activity.sections.map((section, si) =>
                si !== sectionIndex
                  ? section
                  : {
                      ...section,
                      blocks: section.blocks.map((block, bi) =>
                        bi !== blockIndex
                          ? block
                          : {
                              ...block,
                              [key]: value,
                              ...(key === "resourceId"
                                ? { resourceFileId: null }
                                : {}),
                            },
                      ),
                    },
              ),
            },
      ),
    }));
  };

  const handleBlockUpdate = <K extends keyof FormBlockUI>(
    activityIndex: number,
    blockIndex: number,
    key: K,
    value: any,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex ? block : { ...block, [key]: value },
              ),
            },
      ),
    }));
  };

  const handleMainDevelopmentDomains = (
    activityIndex: number,
    value: ActivityMainDevelopmentDomain[],
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((a, i) =>
        i !== activityIndex ? a : { ...a, mainDevelopmentDomains: value },
      ),
    }));
  };

  const handleInteractionTypes = (
    activityIndex: number,
    value: ActivityInteractionType[],
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((a, i) =>
        i !== activityIndex ? a : { ...a, interactionTypes: value },
      ),
    }));
  };

  const handleTypesOfEducationalActivities = (
    activityIndex: number,
    value: ActivityTypeOfEducationalActivity[],
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((a, i) =>
        i !== activityIndex ? a : { ...a, typesOfEducationalActivities: value },
      ),
    }));
  };

  const handleManipulativesToggle = (
    activityIndex: number,
    checked: boolean,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) => {
        if (i !== activityIndex) return activity;
        return checked
          ? toManipulativesActivity(activity)
          : toRegularActivity(activity);
      }),
    }));
  };

  const handleActivityUpdate = <K extends keyof ActivityEntityInput>(
    activityIndex: number,
    key: K,
    value: ActivityEntityInput[K],
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((a, i) =>
        i !== activityIndex ? a : { ...a, [key]: value },
      ),
    }));
  };

  const handleGenerateSummary = async (activityIndex: number) => {
    const activity = lesson.activities[activityIndex];
    if (!activity.title) {
      notify(
        "error",
        "Error",
        "La actividad debe tener un título para generar el resumen",
      );
      return;
    }
    setGeneratingSummaryActivityId(activity.id ?? null);
    try {
      const allBlocks = activity.has_manipulatives
        ? activity.sections.flatMap((s) => s.blocks)
        : activity.blocks;
      const instructions = allBlocks
        .map((b) => b.text)
        .filter((t): t is string => !!t)
        .join(" ")
        .substring(0, 500);
      const materials = allBlocks
        .map((b) => b.materials)
        .filter((m): m is string => !!m)
        .join(", ")
        .substring(0, 200);
      const typeNames: Record<string, string> = {
        START: "Actividad de inicio",
        MIDDLE: "Actividad de desarrollo",
        CLOSING: "Actividad de cierre",
      };
      const response = await generateActivitySummary({
        title: activity.title,
        activityType: typeNames[activity.activityType] || activity.activityType,
        instructions,
        materials,
      });
      if (response.summary) {
        const isTarget = matchActivity(activity, activityIndex);
        setLesson((prev) => ({
          ...prev,
          activities: prev.activities.map((a, i) =>
            isTarget(a, i) ? { ...a, summary: response.summary || "" } : a,
          ),
        }));
        notify(
          "success",
          "Resumen generado",
          "El resumen de la actividad ha sido generado exitosamente",
        );
      } else {
        notify(
          "warning",
          "Sin resumen",
          "No se pudo generar un resumen para esta actividad",
        );
      }
    } catch (error) {
      console.error("Error generando resumen:", error);
      notify(
        "error",
        "Error",
        "Ocurrió un error al generar el resumen de la actividad",
      );
    } finally {
      setGeneratingSummaryActivityId(null);
    }
  };

  const handleGenerateLessonSummary = async () => {
    if (!lesson.title) {
      notify(
        "error",
        "Error",
        "La lección debe tener un título para generar el resumen",
      );
      return;
    }
    setIsGeneratingLessonSummary(true);
    try {
      const activitiesPayload = lesson.activities.map((act, idx) => {
        const allBlocks = act.has_manipulatives
          ? act.sections.flatMap((s) => s.blocks)
          : act.blocks;
        const instructions = allBlocks
          .map((b) => b.text)
          .filter((t): t is string => !!t)
          .join(" ")
          .substring(0, 500);
        const materials = allBlocks
          .map((b) => b.materials)
          .filter((m): m is string => !!m)
          .join(", ")
          .substring(0, 200);
        return {
          title: act.title,
          activityType: act.activityType,
          orderBy: idx,
          instructions,
          materials,
          isGame: false,
        };
      });
      const response = await generateLessonSummary({
        lessonTitle: lesson.title,
        lessonGoal: lesson.objectives_lesson || "",
        activities: activitiesPayload,
      });
      if (response.lines) {
        setLesson((prev) => ({ ...prev, summary: response.lines!.join("\n") }));
        notify(
          "success",
          "Resumen generado",
          "El resumen de la lección ha sido generado exitosamente",
        );
      } else {
        notify(
          "warning",
          "Sin resumen",
          "No se pudo generar un resumen de lección",
        );
      }
    } catch (error) {
      console.error("Error generando resumen lección:", error);
      notify(
        "error",
        "Error",
        "Ocurrió un error al generar el resumen de la lección",
      );
    } finally {
      setIsGeneratingLessonSummary(false);
    }
  };

  const handleChangePlanningSummaryText = (
    activityType: ActivityType,
    text: string,
  ) => {
    setPlanningSummaries((prev) => ({
      ...prev,
      [activityType]: { ...prev[activityType], text },
    }));
  };

  const handleGeneratePlanningSummary = async (activityType: ActivityType) => {
    if (!lessonId) return;
    setGeneratingPlanningSummaryType(activityType);
    try {
      const response = await regeneratePlanningSummary(Number(lessonId), {
        activityType,
      });
      // Generation no longer persists server-side (BE-11): the returned text
      // is an unsaved change until Guardar saves it. `planningSummariesLoadedRef`
      // is deliberately left untouched so handleSubmit's diff against the
      // last-loaded snapshot still detects and saves this as a change.
      setPlanningSummaries((prev) => ({
        ...prev,
        [activityType]: { text: response.text },
      }));
      notify(
        "success",
        "Resumen generado",
        "El resumen de la sección ha sido generado exitosamente.",
      );
    } catch (error) {
      const { title, description } =
        resolvePlanningSummaryErrorNotification(error);
      notify("error", title, description);
    } finally {
      setGeneratingPlanningSummaryType(null);
    }
  };

  const handleChangePlanningSuperSummaryText = (
    activityType: ActivityType,
    text: string,
  ) => {
    setPlanningSuperSummaries((prev) => ({
      ...prev,
      [activityType]: { ...prev[activityType], text },
    }));
  };

  const handleGeneratePlanningSuperSummary = async (
    activityType: ActivityType,
  ) => {
    if (!lessonId) return;
    setGeneratingPlanningSuperSummaryType(activityType);
    try {
      const response = await regeneratePlanningSuperSummary(Number(lessonId), {
        activityType,
      });
      // This endpoint persists server-side, so the returned text is already
      // saved: it becomes the loaded baseline and Guardar will not resend it.
      const saved: PlanningSuperSummary = {
        text: response.text,
        manual: response.manual,
        stale: response.stale,
      };
      planningSuperSummariesLoadedRef.current = {
        ...planningSuperSummariesLoadedRef.current,
        [activityType]: saved,
      };
      setPlanningSuperSummaries((prev) => ({ ...prev, [activityType]: saved }));
      notify(
        "success",
        "Resumen generado",
        "El resumen súper resumido de la sección ha sido generado y guardado.",
      );
    } catch (error) {
      const { title, description } =
        resolvePlanningSummaryErrorNotification(error);
      notify("error", title, description);
    } finally {
      setGeneratingPlanningSuperSummaryType(null);
    }
  };

  const handleBlockTypeChange = (
    activityIndex: number,
    blockIndex: number,
    type: BlockType,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex ? block : { ...block, type },
              ),
            },
      ),
    }));
  };

  const handleBlockMaterialsChange = (
    activityIndex: number,
    blockIndex: number,
    materials: string,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex ? block : { ...block, materials },
              ),
            },
      ),
    }));
  };

  const handleBlockTextChange = (
    activityIndex: number,
    blockIndex: number,
    text: string,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex ? block : { ...block, text },
              ),
            },
      ),
    }));
  };

  const handleBlockFileIdChange = (
    activityIndex: number,
    blockIndex: number,
    file_id: string,
    fileUrl?: string,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex
                  ? block
                  : { ...block, file_id, file_id_url: fileUrl ?? null },
              ),
            },
      ),
    }));
    if (fileUrl && file_id)
      setFileUrls((prev) => ({ ...prev, [file_id]: fileUrl }));
  };

  const haveFileIdInBlock = (activityIndex: number, blockIndex: number) => {
    const file_id = lesson.activities[activityIndex].blocks[blockIndex].file_id;
    return file_id?.length
      ? { haveFile: true, file_id }
      : { haveFile: false, file_id: "" };
  };

  const handleResourceIdsChange = (
    activityIndex: number,
    blockIndex: number,
    resourceId: number | null,
  ) => {
    if (resourceId) {
      const sel = resources.find((r) => r.id === resourceId);
      if (sel?.type === ResourceType.DOWNLOAD) loadResourceDetails(resourceId);
    }
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex
                  ? block
                  : { ...block, resourceId, resourceFileId: null },
              ),
            },
      ),
    }));
  };

  const handleResourceFileIdChange = (
    activityIndex: number,
    blockIndex: number,
    resourceFileId: number | null,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i !== activityIndex
          ? activity
          : {
              ...activity,
              blocks: activity.blocks.map((block, bi) =>
                bi !== blockIndex ? block : { ...block, resourceFileId },
              ),
            },
      ),
    }));
  };

  /**
   * SCRUM-795: envoltorios sobre las transformaciones puras de
   * `alternativesState`. Los ids de las filas nuevas salen de `Date.now()`,
   * igual que para actividades y bloques, y el guardado los descarta porque
   * quedan por encima de cualquier id real.
   */
  const addAlternative = (activityIndex: number) => {
    setLesson((prev) => ({
      ...prev,
      activities: addAlternativeToActivities(
        prev.activities,
        activityIndex,
        Date.now(),
      ),
    }));
  };

  const removeAlternative = (
    activityIndex: number,
    alternativeIndex: number,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: removeAlternativeFromActivities(
        prev.activities,
        activityIndex,
        alternativeIndex,
      ),
    }));
  };

  const handleAlternativeUpdate = <K extends keyof FormAlternativeUI>(
    activityIndex: number,
    alternativeIndex: number,
    key: K,
    value: FormAlternativeUI[K],
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: updateAlternativeInActivities(
        prev.activities,
        activityIndex,
        alternativeIndex,
        key,
        value,
      ),
    }));
  };

  const addAlternativeBlock = (
    activityIndex: number,
    alternativeIndex: number,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: addAlternativeBlockToActivities(
        prev.activities,
        activityIndex,
        alternativeIndex,
        Date.now(),
      ),
    }));
  };

  const removeAlternativeBlock = (
    activityIndex: number,
    alternativeIndex: number,
    blockIndex: number,
  ) => {
    setLesson((prev) => ({
      ...prev,
      activities: removeAlternativeBlockFromActivities(
        prev.activities,
        activityIndex,
        alternativeIndex,
        blockIndex,
      ),
    }));
  };

  const handleAlternativeBlockUpdate = <K extends keyof FormBlockUI>(
    activityIndex: number,
    alternativeIndex: number,
    blockIndex: number,
    key: K,
    value: FormBlockUI[K],
  ) => {
    if (key === "resourceId" && value) {
      const selected = resources.find((r) => r.id === (value as number));
      if (selected?.type === ResourceType.DOWNLOAD)
        loadResourceDetails(value as number);
    }
    setLesson((prev) => ({
      ...prev,
      activities: updateAlternativeBlockInActivities(
        prev.activities,
        activityIndex,
        alternativeIndex,
        blockIndex,
        key,
        value,
      ),
    }));
  };

  const handleGenerateMaterials = async (activityIndex: number) => {
    const activity = lesson.activities[activityIndex];
    if (!activity.has_manipulatives) return;
    setGeneratingMaterialsActivityId(activity.id ?? null);
    try {
      const materialsText = activity.sections
        .flatMap((s) => s.blocks.map((b) => b.materials || ""))
        .join("\n---\n");
      const response = await generateManipulativeMaterials({ materialsText });
      if (response.materials) {
        const isTarget = matchActivity(activity, activityIndex);
        setLesson((prev) => ({
          ...prev,
          activities: prev.activities.map((act, i) =>
            isTarget(act, i)
              ? {
                  ...act,
                  manipulative_materials_list: response.materials || "",
                }
              : act,
          ),
        }));
        notify(
          "success",
          "Materiales generados",
          "Los materiales manipulativos han sido identificados",
        );
      } else {
        notify(
          "warning",
          "Sin materiales",
          "No se pudieron identificar manipulativos",
        );
      }
    } catch (error) {
      console.error("Error generando materiales manipulativos:", error);
      notify(
        "error",
        "Error",
        "Ocurrió un error al generar los materiales manipulativos",
      );
    } finally {
      setGeneratingMaterialsActivityId(null);
    }
  };

  const handleSuggestPills = async (activityIndex: number) => {
    const activity = lesson.activities[activityIndex];
    setPillLoading((prev) => ({ ...prev, [activityIndex]: true }));
    try {
      const allBlocks = activity.has_manipulatives
        ? activity.sections.flatMap((s) => s.blocks)
        : activity.blocks;
      const descripcion = allBlocks
        .map((b) => b.text)
        .filter((t): t is string => !!t)
        .join(" ")
        .substring(0, 500);
      const materiales = allBlocks
        .map((b) => b.materials)
        .filter((m): m is string => !!m)
        .join(", ")
        .substring(0, 200);
      if (!educationLevel) {
        notify(
          "error",
          "Error de validación",
          "Debe seleccionar un grado antes de pedir sugerencias IA."
        );
        setPillLoading((prev) => ({ ...prev, [activityIndex]: false }));
        return;
      }
      const input = {
        education_level: educationLevel,
        titulo: activity.title,
        descripcion,
        materiales,
        duracion: activity.duration || 0,
      };
      const result = await sugerirPastillasIA(input);
      const targetIndex = currentActivityIndex(activity, activityIndex);
      if (targetIndex !== -1) {
        setPillSuggestions((prev) => ({ ...prev, [targetIndex]: result }));
      }
      notify(
        "success",
        "Sugerencias IA aplicadas",
        "Las opciones sugeridas han sido cargadas."
      );
    } catch {
      notify("error", "Error IA", "No se pudo obtener sugerencia IA.");
    } finally {
      const targetIndex = currentActivityIndex(activity, activityIndex);
      if (targetIndex !== -1) {
        setPillLoading((prev) => ({ ...prev, [targetIndex]: false }));
      }
    }
  };

  const handleSubmitError = (error: any) => {
    if (error instanceof LessonConflictError) {
      notify(
        "error",
        "Lección modificada",
        "Esta lección fue modificada por alguien más. Recarga la página para ver la versión actual y vuelve a aplicar tus cambios.",
      );
      return;
    }
    if (error instanceof ResourceHomeworkConflictError) {
      setResourceImpactModal({ impact: error.resourceImpact });
      return;
    }
    console.error(error);
    notify(
      "error",
      "Error",
      error?.response?.data?.message ||
        error?.message ||
        "Ocurrió un error inesperado.",
    );
  };

  const handleResourceImpactConfirm = async (
    resolutions: HomeworkResolutionDto[],
  ) => {
    const lastSubmit = lastSubmitPayloadRef.current;
    if (!lastSubmit) return;
    // Closed optimistically: if the retry hits a NEW resource conflict,
    // handleSubmitError re-opens it with the fresh impact.
    setResourceImpactModal(null);
    setIsRetryingSubmit(true);
    try {
      await handleSubmitFormResponseManage(
        { ...lastSubmit.data, homework_resolutions: resolutions },
        createLesson,
        updateLesson,
        navigate,
        dispatch,
        "lessons",
        "createLessonSuccess",
        "updateLessonSuccess",
        lastSubmit.id,
        handleSubmitError,
      );
    } finally {
      setIsRetryingSubmit(false);
    }
  };

  const handleSubmit = async () => {
    const { validate, description, message } = validateForm(
      lesson,
      setActiveTab,
      { skipStageActivityCheck: diagnosticLesson },
    );
    if (!validate) {
      notify("error", message, description);
      return;
    }
    try {
      setIsLoading(true);
      const isEdit = !!lessonId;
      // Merge pillSuggestions into lesson activities before submitting
      const lessonToSubmit = {
        ...lesson,
        education_level: educationLevel,
        diagnostic: diagnosticLesson,
        ...(isEdit && lessonUpdatedAtRef.current
          ? { expected_updated_at: lessonUpdatedAtRef.current }
          : {}),
        // Summaries ride the lesson save instead of their own PATCH: a
        // separate request per section used to bump Lesson.updated_at
        // *before* this PATCH, self-inflicting a 409 on the very next line.
        ...buildPlanningSummaryLessonFields(
          planningSummaries,
          planningSummariesLoadedRef.current,
        ),
        ...buildPlanningSuperSummaryLessonFields(
          planningSuperSummaries,
          planningSuperSummariesLoadedRef.current,
        ),
        activities: lesson.activities.map((activity, index) => {
          const pills = pillSuggestions[index] || {};
          // Si es KINDER, mergear los campos sugeridos
          if (educationLevel === "KINDER") {
            return {
              ...activity,
              mainDevelopmentDomains:
                pills.main_development_domain ||
                activity.mainDevelopmentDomains ||
                [],
              interactionTypes:
                pills.interaction_type || activity.interactionTypes || [],
              typesOfEducationalActivities:
                pills.type_of_educational_activity ||
                activity.typesOfEducationalActivities ||
                [],
            };
          }
          // Si es PRIMARIA, mergear los campos sugeridos
          return {
            ...activity,
            bloom_level: pills.bloom_level || activity.bloom_level || [],
            primaria_interaction_type:
              pills.primaria_interaction_type ||
              activity.primaria_interaction_type ||
              [],
            evidence_type: pills.evidence_type || activity.evidence_type || [],
            activity_nature:
              pills.activity_nature || activity.activity_nature || [],
          };
        }),
      };
      lastSubmitPayloadRef.current = {
        id: isEdit ? Number(lessonId) : undefined,
        data: lessonToSubmit,
      };
      await handleSubmitFormResponseManage(
        lessonToSubmit,
        createLesson,
        updateLesson,
        navigate,
        dispatch,
        "lessons",
        "createLessonSuccess",
        "updateLessonSuccess",
        isEdit ? Number(lessonId) : undefined,
        handleSubmitError,
      );
    } catch (error) {
      console.error(error);
      notify("error", "Error", "Ocurrió un error al guardar la lección.");
    } finally {
      setIsLoading(false);
    }
  };

  const value: LessonFormContextValue = {
    lesson,
    setLesson,
    educationLevel,
    setEducationLevel,
    pillSuggestions,
    setPillSuggestions,
    pillLoading,
    setPillLoading,
    activeTab,
    setActiveTab,
    diagnosticLesson,
    setDiagnosticLesson,
    isLoading,
    currentLessonId: lessonId ? Number(lessonId) : null,
    planningSummaries,
    generatingPlanningSummaryType,
    handleGeneratePlanningSummary,
    handleChangePlanningSummaryText,
    planningSuperSummaries,
    generatingPlanningSuperSummaryType,
    handleGeneratePlanningSuperSummary,
    handleChangePlanningSuperSummaryText,
    generatingSummaryActivityId,
    isGeneratingLessonSummary,
    generatingMaterialsActivityId,
    resourceTypeFilter,
    setResourceTypeFilter,
    resource,
    fileUrls,
    setFileUrls,
    resources,
    resourceDetails,
    addActivity,
    removeActivity,
    reorderActivities,
    addBlock,
    removeBlock,
    addSectionBlock,
    removeSectionBlock,
    handleSectionBlockUpdate,
    handleBlockUpdate,
    handleMainDevelopmentDomains,
    handleInteractionTypes,
    handleTypesOfEducationalActivities,
    handleManipulativesToggle,
    handleActivityUpdate,
    handleGenerateSummary,
    handleGenerateLessonSummary,
    handleBlockTypeChange,
    handleBlockMaterialsChange,
    handleBlockTextChange,
    handleBlockFileIdChange,
    haveFileIdInBlock,
    loadResourceDetails,
    handleResourceIdsChange,
    handleResourceFileIdChange,
    addAlternative,
    removeAlternative,
    handleAlternativeUpdate,
    addAlternativeBlock,
    removeAlternativeBlock,
    handleAlternativeBlockUpdate,
    handleGenerateMaterials,
    handleSubmit,
    handleSuggestPills,
    customUpload,
  };

  return (
    <LessonFormContext value={value}>
      {children}
      <ResourceHomeworkImpactModal
        open={!!resourceImpactModal}
        impact={resourceImpactModal?.impact ?? []}
        confirmLoading={isRetryingSubmit}
        onCancel={() => setResourceImpactModal(null)}
        onConfirm={handleResourceImpactConfirm}
      />
    </LessonFormContext>
  );
};
